import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../store/storeSlice';
import { toggleDarkMode } from '../store/settingsSlice';
import { signin } from '../http/userApi';
import {
    Box,
    TextField,
    Button,
    Typography,
    IconButton,
    InputAdornment,
    Alert,
    Snackbar,
    Slide,
    Fade
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    LightMode,
    DarkMode,
    Person,
    Lock
} from '@mui/icons-material';

const MinimalLoginForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isDarkMode = useSelector(state => state.settings.darkMode);

    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!login.trim() || !password.trim()) {
            setError('Заполните все поля');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const res = await signin(login, password);
            dispatch(setUser({
                ...res,
                isAdmin: res.roles.includes('admin'),
                isAuth: true
            }));
            navigate('/map');
        } catch (e) {
            console.error('Login error:', e);
            const errorMessage = e.response?.data?.message || 'Неверный логин или пароль';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            handleSubmit(e);
        }
    };

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Theme Toggle */}
            <Fade in={isVisible} timeout={800}>
                <IconButton
                    onClick={() => dispatch(toggleDarkMode())}
                    sx={{
                        position: 'absolute',
                        top: 24,
                        right: 24,
                        color: isDarkMode ? '#ffffff' : '#000000',
                        backgroundColor: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.1)' 
                            : 'rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                        '&:hover': {
                            backgroundColor: isDarkMode 
                                ? 'rgba(255, 255, 255, 0.2)' 
                                : 'rgba(0, 0, 0, 0.2)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    {isDarkMode ? <LightMode /> : <DarkMode />}
                </IconButton>
            </Fade>

            {/* Login Form */}
            <Slide direction="up" in={isVisible} timeout={600}>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        width: '100%',
                        maxWidth: 400,
                        px: 3,
                        py: 4,
                        backgroundColor: isDarkMode 
                            ? 'rgba(30, 30, 30, 0.95)' 
                            : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: 2,
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        boxShadow: isDarkMode 
                            ? '0 20px 40px rgba(0, 0, 0, 0.3)' 
                            : '0 20px 40px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                    }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Fade in={isVisible} timeout={1000}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 300,
                                    color: isDarkMode ? '#ffffff' : '#000000',
                                    letterSpacing: '-0.02em',
                                    mb: 1
                                }}
                            >
                                Вход в систему
                            </Typography>
                        </Fade>
                        <Fade in={isVisible} timeout={1200}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                    fontWeight: 400
                                }}
                            >
                                Система мониторинга
                            </Typography>
                        </Fade>
                    </Box>

                    {/* Login Field */}
                    <Fade in={isVisible} timeout={1400}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Логин"
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person 
                                            sx={{ 
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                fontSize: 20
                                            }} 
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 1,
                                    '& fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                        borderWidth: 1,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#ffffff' : '#000000',
                                        borderWidth: 2,
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: isDarkMode ? '#ffffff' : '#000000',
                                    fontSize: 16,
                                    py: 1.5,
                                    '&::placeholder': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Fade>

                    {/* Password Field */}
                    <Fade in={isVisible} timeout={1600}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Пароль"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock 
                                            sx={{ 
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                fontSize: 20
                                            }} 
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                '&:hover': {
                                                    color: isDarkMode ? '#ffffff' : '#000000',
                                                },
                                            }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 1,
                                    '& fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                        borderWidth: 1,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#ffffff' : '#000000',
                                        borderWidth: 2,
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: isDarkMode ? '#ffffff' : '#000000',
                                    fontSize: 16,
                                    py: 1.5,
                                    '&::placeholder': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Fade>

                    {/* Submit Button */}
                    <Fade in={isVisible} timeout={1800}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            startIcon={<LoginIcon />}
                            sx={{
                                height: 48,
                                backgroundColor: isDarkMode ? '#ffffff' : '#000000',
                                color: isDarkMode ? '#000000' : '#ffffff',
                                borderRadius: 1,
                                fontSize: 16,
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                                    boxShadow: 'none',
                                },
                                '&:disabled': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                                    color: isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {isLoading ? 'Вход...' : 'Войти'}
                        </Button>
                    </Fade>
                </Box>
            </Slide>

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                TransitionComponent={Slide}
            >
                <Alert
                    onClose={() => setError('')}
                    severity="error"
                    sx={{
                        backgroundColor: isDarkMode ? '#2d2d2d' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                        borderRadius: 1,
                        '& .MuiAlert-icon': {
                            color: '#f44336',
                        },
                    }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MinimalLoginForm;
